import React from "react";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Skeleton from "@mui/material/Skeleton";
import { IconButton, Box, Typography, Tooltip, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useQuery } from "@tanstack/react-query";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import ButtonView from "../../../common/Button";
import { Column } from "../../../common/Table/types";
import { Data } from "../../types";
import TableView from "../../../common/Table";
import { projectsService } from "../../services";
import ProjectRow from "./parts/ProjectRow";
import { formatDateAndTime } from "../../../../utils/formatDate";
import RoleBasedAccess from "../../../common/RoleBasedAccess";
import { RoleName } from "../../../common/enums/RoleName";

const columns: Column[] = [
  { id: "name", label: "Name", minWidth: 100 },
  { id: "description", label: "Description", minWidth: 100 },
  { id: "created_at", label: "Created At", minWidth: 100 },
  { id: "number_of_episodes", label: "Episodes", minWidth: 100 },
  { id: "action", label: "Action", minWidth: 10 },
];

const SKELETON_ROWS = 6;
const colWidths = ["25%", "35%", "18%", "12%", "10%"];

function TableSkeletonLoader() {
  return (
    <Table>
      <TableHead>
        <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
          {colWidths.map((w) => (
            <TableCell key={w} sx={{ py: 1.25, px: 2, width: w }}>
              <Skeleton variant="text" width="60%" height={14} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={rowIdx} sx={{ "&:last-child td": { border: 0 } }}>
            {/* Name */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="75%" height={14} />
            </TableCell>
            {/* Description */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="90%" height={14} />
              <Skeleton
                variant="text"
                width="60%"
                height={12}
                sx={{ mt: 0.4 }}
              />
            </TableCell>
            {/* Created At */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width="80%" height={14} />
            </TableCell>
            {/* Episodes count */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Skeleton variant="text" width={28} height={14} />
            </TableCell>
            {/* Actions */}
            <TableCell sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function createData(
  id: string,
  name: string,
  description: string,
  created_at: string,
  number_of_episodes: number,
): Data {
  return { id, name, description, created_at, number_of_episodes };
}

interface Props {
  handleProjectCreateDialog: () => void;
}

export default function ProjectsList({ handleProjectCreateDialog }: Props) {
  const theme = useTheme();
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["projects", page, rowsPerPage],
    queryFn: () =>
      projectsService.fetchProjects({
        page,
        perPage: rowsPerPage,
      }),
  });

  const rows =
    data?.projects?.map((project) =>
      createData(
        project.id,
        project.name,
        project.description,
        formatDateAndTime(project.created_at),
        project.metadata.number_of_episodes,
      ),
    ) || [];

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await refetch();
      const elapsed = Date.now() - startTime;
      if (elapsed < 500) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 500 - elapsed);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const totalItems = data?.pagination?.total_items || 0;

  return (
    <>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <Box sx={{ flex: 1 }} />

        <Tooltip title="Refresh">
          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            size="small"
            sx={{
              width: 36,
              height: 36,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              backgroundColor: "#fff",
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: "#2563eb",
              },
              "&:disabled": { backgroundColor: "#f9fafb" },
            }}
          >
            <RefreshIcon
              sx={{
                fontSize: 18,
                animation: isRefreshing ? "spin 0.8s linear infinite" : "none",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Tooltip>

        <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
          <ButtonView
            type="button"
            variant="contained"
            size="small"
            label="Add Project"
            startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
            handleOnClick={handleProjectCreateDialog}
            sx={{
              backgroundColor: "#2563eb",
              borderRadius: "8px",
              px: 2,
              py: 0.75,
              fontSize: "13px",
              fontWeight: 600,
              height: 36,
              whiteSpace: "nowrap",
              boxShadow: "0 1px 3px rgba(37, 99, 235, 0.3)",
              "&:hover": { backgroundColor: "#1d4ed8" },
            }}
          />
        </RoleBasedAccess>
      </Box>

      {/* Table card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}
          >
            All Projects
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", mt: 0.25 }}
          >
            {totalItems} projects total
          </Typography>
        </Box>

        {isLoading || isRefreshing ? (
          <TableContainer>
            <TableSkeletonLoader />
          </TableContainer>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <TableView
                columns={columns}
                rows={rows}
                handleClick={() => {}}
                TableList={ProjectRow}
                handleCustomEvent={() => {}}
              />
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                "& .MuiTablePagination-toolbar": { minHeight: 48 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  { fontSize: "12px" },
              }}
            />
          </>
        )}
      </Paper>
    </>
  );
}
